/**
 * Password Policy Enforcement
 * SOC 2 Controls: CC6.1
 * ISO 27001: A.9.4.3
 *
 * Enforces strong password requirements for compliance with security standards.
 */

// =============================================================================
// POLICY CONFIGURATION
// =============================================================================

export interface PasswordPolicy {
  minLength: number;
  maxLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumber: boolean;
  requireSpecial: boolean;
  preventCommonPasswords: boolean;
  minStrengthScore: number; // 0-4 (zxcvbn scale)
}

export const DEFAULT_PASSWORD_POLICY: PasswordPolicy = {
  minLength: 12,
  maxLength: 128,
  requireUppercase: true,
  requireLowercase: true,
  requireNumber: true,
  requireSpecial: true,
  preventCommonPasswords: true,
  minStrengthScore: 3, // "strong" on zxcvbn scale
};

// For backward compatibility during migration, use relaxed policy
export const MIGRATION_PASSWORD_POLICY: PasswordPolicy = {
  minLength: 8,
  maxLength: 128,
  requireUppercase: true,
  requireLowercase: true,
  requireNumber: true,
  requireSpecial: false,
  preventCommonPasswords: true,
  minStrengthScore: 2,
};

// =============================================================================
// COMMON PASSWORDS LIST (top 100 most common)
// =============================================================================

const COMMON_PASSWORDS = new Set([
  'password', 'password1', 'password123', '123456', '12345678', '123456789',
  '1234567890', 'qwerty', 'qwerty123', 'abc123', 'monkey', 'master', 'dragon',
  'letmein', 'login', 'welcome', 'admin', 'administrator', 'passw0rd',
  'shadow', 'sunshine', 'princess', 'football', 'baseball', 'soccer',
  'hockey', 'batman', 'trustno1', 'superman', 'hunter', 'hunter2',
  'charlie', 'andrew', 'joshua', 'michael', 'michelle', 'jennifer',
  'jessica', 'ashley', 'amanda', 'daniel', 'david', 'matthew', 'jordan',
  'access', 'iloveyou', 'starwars', 'nintendo', 'computer', 'internet',
  'cheese', 'pepper', 'cookie', 'chicken', 'summer', 'winter', 'spring',
  'autumn', 'october', 'december', 'january', 'february', 'march', 'april',
  'friday', 'monday', 'sunday', 'asdfgh', 'asdfghjkl', 'zxcvbn', 'zxcvbnm',
  '111111', '000000', '121212', '123123', '654321', '666666', '696969',
  '777777', '888888', '999999', 'whatever', 'nothing', 'secret', 'test',
  'test123', 'hello', 'hello123', 'freedom', 'matrix', 'miller', 'flower',
  'killer', 'diamond', 'george', 'robert', 'thomas', 'william', 'richard',
  'charles', 'joseph', 'jesus', 'bitcoin', 'google', 'apple', 'amazon',
]);

// =============================================================================
// VALIDATION RESULT TYPE
// =============================================================================

export interface PasswordValidationResult {
  valid: boolean;
  score: number; // 0-4 strength score
  errors: string[];
  suggestions: string[];
  strengthLabel: 'very weak' | 'weak' | 'fair' | 'strong' | 'very strong';
}

// =============================================================================
// VALIDATION FUNCTIONS
// =============================================================================

/**
 * Validate a password against the policy
 */
export function validatePassword(
  password: string,
  policy: PasswordPolicy = DEFAULT_PASSWORD_POLICY
): PasswordValidationResult {
  const errors: string[] = [];
  const suggestions: string[] = [];

  // Length checks
  if (password.length < policy.minLength) {
    errors.push(`Password must be at least ${policy.minLength} characters`);
  }
  if (password.length > policy.maxLength) {
    errors.push(`Password must be no more than ${policy.maxLength} characters`);
  }

  // Character type checks
  if (policy.requireUppercase && !/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }
  if (policy.requireLowercase && !/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }
  if (policy.requireNumber && !/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number');
  }
  if (policy.requireSpecial && !/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(password)) {
    errors.push('Password must contain at least one special character (!@#$%^&*...)');
  }

  // Common password check
  if (policy.preventCommonPasswords && COMMON_PASSWORDS.has(password.toLowerCase())) {
    errors.push('This password is too common and easily guessed');
  }

  // Calculate strength score (simplified zxcvbn-like scoring)
  const score = calculatePasswordStrength(password);

  if (score < policy.minStrengthScore) {
    suggestions.push('Consider using a longer password with more variety');
    if (!/[A-Z]/.test(password)) suggestions.push('Add uppercase letters');
    if (!/[0-9]/.test(password)) suggestions.push('Add numbers');
    if (!/[!@#$%^&*]/.test(password)) suggestions.push('Add special characters');
  }

  const strengthLabels: PasswordValidationResult['strengthLabel'][] = [
    'very weak', 'weak', 'fair', 'strong', 'very strong'
  ];

  return {
    valid: errors.length === 0 && score >= policy.minStrengthScore,
    score,
    errors,
    suggestions,
    strengthLabel: strengthLabels[score] || 'very weak',
  };
}

/**
 * Calculate password strength score (0-4)
 * Simplified version of zxcvbn scoring
 */
export function calculatePasswordStrength(password: string): number {
  if (!password) return 0;

  let score = 0;
  const length = password.length;

  // Length-based scoring
  if (length >= 8) score += 1;
  if (length >= 12) score += 1;
  if (length >= 16) score += 1;

  // Character variety scoring
  const hasLower = /[a-z]/.test(password);
  const hasUpper = /[A-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(password);

  const varietyCount = [hasLower, hasUpper, hasNumber, hasSpecial].filter(Boolean).length;
  if (varietyCount >= 3) score += 1;
  if (varietyCount === 4) score += 1;

  // Penalize common patterns
  if (/^[a-z]+$/i.test(password)) score = Math.max(0, score - 1); // Only letters
  if (/^[0-9]+$/.test(password)) score = Math.max(0, score - 2); // Only numbers
  if (/(.)\1{2,}/.test(password)) score = Math.max(0, score - 1); // Repeated characters
  if (/^(123|abc|qwe)/i.test(password)) score = Math.max(0, score - 1); // Common sequences

  // Common password check
  if (COMMON_PASSWORDS.has(password.toLowerCase())) {
    score = 0;
  }

  return Math.min(4, Math.max(0, score));
}

/**
 * Get password requirements as a formatted list
 */
export function getPasswordRequirements(policy: PasswordPolicy = DEFAULT_PASSWORD_POLICY): string[] {
  const requirements: string[] = [];

  requirements.push(`At least ${policy.minLength} characters`);
  if (policy.requireUppercase) requirements.push('At least one uppercase letter (A-Z)');
  if (policy.requireLowercase) requirements.push('At least one lowercase letter (a-z)');
  if (policy.requireNumber) requirements.push('At least one number (0-9)');
  if (policy.requireSpecial) requirements.push('At least one special character (!@#$%^&*)');
  if (policy.preventCommonPasswords) requirements.push('Cannot be a commonly used password');

  return requirements;
}

/**
 * Check if password meets minimum requirements for form submission
 * (less strict than full validation, for UX)
 */
export function meetsMinimumRequirements(password: string): boolean {
  return password.length >= 8;
}

// =============================================================================
// ZOD SCHEMA FOR FORM VALIDATION
// =============================================================================

import { z } from 'zod';

export const passwordSchema = z
  .string()
  .min(12, 'Password must be at least 12 characters')
  .max(128, 'Password must be no more than 128 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')
  .regex(
    /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/,
    'Password must contain at least one special character'
  )
  .refine(
    (password) => !COMMON_PASSWORDS.has(password.toLowerCase()),
    'This password is too common. Please choose a stronger password.'
  );

// Relaxed schema for migration period
export const migrationPasswordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(128, 'Password must be no more than 128 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')
  .refine(
    (password) => !COMMON_PASSWORDS.has(password.toLowerCase()),
    'This password is too common. Please choose a stronger password.'
  );
